/**
 * Sorts two objects based on their date properties in ascending or descending order.
 *
 * @param {T} a - The first object to compare.
 * @param {T} b - The second object to compare.
 * @param {'ascending' | 'descending'} sortOrder - The desired sort order, either 'ascending' or 'descending'.
 * @param {(item: T) => string} getDateFn - A function to extract the date string from the object.
 * @returns {number} A positive number if a is greater, a negative number if b is greater, or 0 if they are equal.
 */
export function sortByDate<T>(
  a: T,
  b: T,
  sortOrder: "ascending" | "descending",
  getDateFn: (item: T) => string
) {
  const dateA = parseDate(getDateFn(a)).getTime();
  const dateB = parseDate(getDateFn(b)).getTime();

  if (sortOrder === "ascending") {
    return dateA - dateB;
  } else {
    return dateB - dateA;
  }
}

/**
 * Parses a date string in the format 'dd.mm.yyyy' into a Date object.
 *
 * @param {string} dateString - The date string to parse.
 * @returns {Date} A Date object representing the parsed date.
 */
export const parseDate = (dateString: string): Date => {
  const [day, month, year] = dateString.split(".").map(Number);
  return new Date(year, month - 1, day);
};
